import React, { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Shield,
  MessageSquare,
  Database,
  Clock,
  Trash2,
  CreditCard,
  RefreshCw,
  Plus,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { Modal, ConfirmModal } from '@/components/ui/Modal';
import {
  useSettings,
  useUpdateSetting,
  useDeleteSetting,
  useInvalidateCache,
  useStripeStatus,
  useUpdateStripeConfig,
} from '@/hooks/useSettings';
import type { SystemSetting } from '@/types/admin.types';

interface SettingGroup {
  title: string;
  icon: React.ReactNode;
  category: string;
  description?: string;
}

const settingGroups: SettingGroup[] = [
  {
    title: 'Rate Limiting',
    icon: <Shield className="w-5 h-5" />,
    category: 'rate_limit',
    description: 'Controle de taxa de requisicoes',
  },
  {
    title: 'WhatsApp',
    icon: <MessageSquare className="w-5 h-5" />,
    category: 'whatsapp',
    description: 'Configuracoes do provider WhatsApp',
  },
  {
    title: 'Storage',
    icon: <Database className="w-5 h-5" />,
    category: 'storage',
    description: 'Armazenamento de arquivos',
  },
  {
    title: 'Sistema',
    icon: <Clock className="w-5 h-5" />,
    category: 'system',
    description: 'Configuracoes gerais do sistema',
  },
];

const getSettingLabel = (key: string): string => {
  const parts = key.split('.');
  const lastPart = parts[parts.length - 1];
  return lastPart
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const SettingsPage: React.FC = () => {
  const { data: settings, isLoading } = useSettings();
  const { data: stripeStatus } = useStripeStatus();
  const updateMutation = useUpdateSetting();
  const deleteMutation = useDeleteSetting();
  const invalidateCacheMutation = useInvalidateCache();
  const updateStripeConfigMutation = useUpdateStripeConfig();

  const [editedValues, setEditedValues] = useState<Record<string, string | boolean>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);
  const [showAddSettingModal, setShowAddSettingModal] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());

  // New setting form
  const [newSetting, setNewSetting] = useState({
    key: '',
    value: '',
    category: 'system',
    encrypted: false,
  });

  // Stripe credentials
  const [stripeCredentials, setStripeCredentials] = useState({
    secretKey: '',
    webhookSecret: '',
  });

  // Group settings by category
  const settingsByCategory = React.useMemo(() => {
    if (!settings) return {};
    return settings.reduce((acc, setting) => {
      const cat = setting.category || 'other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(setting);
      return acc;
    }, {} as Record<string, SystemSetting[]>);
  }, [settings]);

  // Initialize edited values from settings
  useEffect(() => {
    if (settings) {
      const initial: Record<string, string | boolean> = {};
      settings.forEach((s) => {
        initial[s.key] = s.valueType === 'boolean' ? s.value === 'true' : s.value;
      });
      setEditedValues(initial);
    }
  }, [settings]);

  const handleChange = (key: string, value: string | boolean) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (key: string, setting: SystemSetting) => {
    const value = editedValues[key];
    if (value === undefined) return;

    setSavingKey(key);
    try {
      await updateMutation.mutateAsync({
        key,
        value,
        encrypted: setting.encrypted,
        category: setting.category,
      });
    } finally {
      setSavingKey(null);
    }
  };

  const hasChanges = (key: string, setting: SystemSetting): boolean => {
    const currentValue = setting.valueType === 'boolean' ? setting.value === 'true' : setting.value;
    return editedValues[key] !== currentValue;
  };

  const handleClearCache = async () => {
    await invalidateCacheMutation.mutateAsync();
    setShowClearCacheModal(false);
  };

  const handleAddSetting = async () => {
    if (!newSetting.key || !newSetting.value) return;
    await updateMutation.mutateAsync({
      key: newSetting.key,
      value: newSetting.value,
      category: newSetting.category,
      encrypted: newSetting.encrypted,
    });
    setNewSetting({ key: '', value: '', category: 'system', encrypted: false });
    setShowAddSettingModal(false);
  };

  const handleSaveStripeCredentials = async () => {
    const config: { secretKey?: string; webhookSecret?: string } = {};
    if (stripeCredentials.secretKey) {
      config.secretKey = stripeCredentials.secretKey;
    }
    if (stripeCredentials.webhookSecret) {
      config.webhookSecret = stripeCredentials.webhookSecret;
    }
    await updateStripeConfigMutation.mutateAsync(config);
    setStripeCredentials({ secretKey: '', webhookSecret: '' });
    setShowStripeModal(false);
  };

  const toggleSecretVisibility = (key: string) => {
    setVisibleSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuracoes</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configuracoes gerais do sistema
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowAddSettingModal(true)}
          >
            Nova Config
          </Button>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={() => setShowClearCacheModal(true)}
          >
            Limpar Cache
          </Button>
        </div>
      </div>

      {/* Stripe Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Stripe
            {stripeStatus?.configured ? (
              <Badge variant="success" size="sm">
                <CheckCircle className="w-3 h-3 mr-1" />
                Configurado
              </Badge>
            ) : (
              <Badge variant="danger" size="sm">
                <XCircle className="w-3 h-3 mr-1" />
                Nao Configurado
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {stripeStatus?.hasSecretKey ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span>Secret Key</span>
                </div>
                <div className="flex items-center gap-2">
                  {stripeStatus?.hasWebhookSecret ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span>Webhook Secret</span>
                </div>
                {stripeStatus?.testMode && (
                  <Badge variant="warning" size="sm">Modo Teste</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Configure as credenciais do Stripe para habilitar pagamentos.
              </p>
            </div>
            <Button
              leftIcon={<Lock className="w-4 h-4" />}
              onClick={() => setShowStripeModal(true)}
            >
              Configurar Stripe
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings Groups */}
      {settingGroups.map((group) => {
        const categorySettings = settingsByCategory[group.category] || [];

        return (
          <Card key={group.category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {group.icon}
                {group.title}
                <span className="text-sm font-normal text-gray-500">
                  ({categorySettings.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {categorySettings.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  Nenhuma configuracao nesta categoria
                </p>
              ) : (
                <div className="space-y-6">
                  {categorySettings.map((setting) => {
                    const isBoolean = setting.valueType === 'boolean';
                    const value = editedValues[setting.key];
                    const isSecret = setting.encrypted;
                    const isVisible = visibleSecrets.has(setting.key);

                    return (
                      <div key={setting.key} className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {getSettingLabel(setting.key)}
                            </span>
                            {isSecret && (
                              <Badge variant="warning" size="sm">
                                <Lock className="w-3 h-3 mr-1" />
                                Criptografado
                              </Badge>
                            )}
                          </div>
                          {isBoolean ? (
                            <Checkbox
                              label={setting.description || setting.key}
                              checked={value as boolean}
                              onChange={(e) => handleChange(setting.key, e.target.checked)}
                            />
                          ) : (
                            <div className="relative">
                              <Input
                                type={isSecret && !isVisible ? 'password' : 'text'}
                                value={value as string}
                                onChange={(e) => handleChange(setting.key, e.target.value)}
                                placeholder={isSecret ? '********' : ''}
                              />
                              {isSecret && (
                                <button
                                  type="button"
                                  onClick={() => toggleSecretVisibility(setting.key)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              )}
                            </div>
                          )}
                          {setting.description && !isBoolean && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {setting.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Key: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{setting.key}</code>
                          </p>
                        </div>
                        <div className="flex gap-2 mt-6 sm:mt-0">
                          {hasChanges(setting.key, setting) && (
                            <Button
                              size="sm"
                              leftIcon={<Save className="w-4 h-4" />}
                              onClick={() => handleSave(setting.key, setting)}
                              isLoading={savingKey === setting.key}
                            >
                              Salvar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="danger"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => deleteMutation.mutate(setting.key)}
                            isLoading={deleteMutation.isPending}
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Other Settings (uncategorized) */}
      {settingsByCategory['other']?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Outras Configuracoes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {settingsByCategory['other'].map((setting) => (
                <div key={setting.key} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <span className="font-medium">{setting.key}</span>
                    <span className="text-gray-500 ml-2">{setting.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Settings className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Cuidado ao alterar configuracoes
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Alteracoes nas configuracoes podem afetar o funcionamento do sistema.
                Configuracoes marcadas como criptografadas sao armazenadas de forma segura.
                Algumas configuracoes podem requerer reinicio do servico.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clear Cache Modal */}
      <ConfirmModal
        isOpen={showClearCacheModal}
        onClose={() => setShowClearCacheModal(false)}
        onConfirm={handleClearCache}
        title="Limpar Cache"
        message="Isso vai invalidar o cache de configuracoes e rate limiting. Deseja continuar?"
        confirmText="Limpar Cache"
        variant="danger"
        isLoading={invalidateCacheMutation.isPending}
      />

      {/* Add Setting Modal */}
      <Modal
        isOpen={showAddSettingModal}
        onClose={() => setShowAddSettingModal(false)}
        title="Nova Configuracao"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowAddSettingModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddSetting}
              isLoading={updateMutation.isPending}
              disabled={!newSetting.key || !newSetting.value}
            >
              Criar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Chave"
            value={newSetting.key}
            onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })}
            placeholder="ex: stripe.api_key"
          />
          <Input
            label="Valor"
            type={newSetting.encrypted ? 'password' : 'text'}
            value={newSetting.value}
            onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
          />
          <Input
            label="Categoria"
            value={newSetting.category}
            onChange={(e) => setNewSetting({ ...newSetting, category: e.target.value })}
            placeholder="system, stripe, whatsapp..."
          />
          <Checkbox
            label="Valor criptografado (senhas, chaves de API)"
            checked={newSetting.encrypted}
            onChange={(e) => setNewSetting({ ...newSetting, encrypted: e.target.checked })}
          />
        </div>
      </Modal>

      {/* Stripe Configuration Modal */}
      <Modal
        isOpen={showStripeModal}
        onClose={() => setShowStripeModal(false)}
        title="Configurar Stripe"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowStripeModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveStripeCredentials}
              isLoading={updateStripeConfigMutation.isPending}
              disabled={!stripeCredentials.secretKey && !stripeCredentials.webhookSecret}
            >
              Salvar Credenciais
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              As credenciais serao armazenadas de forma criptografada.
              Use chaves de teste (sk_test_) para ambiente de desenvolvimento.
            </p>
          </div>
          <Input
            label="Secret Key"
            type="password"
            value={stripeCredentials.secretKey}
            onChange={(e) => setStripeCredentials({ ...stripeCredentials, secretKey: e.target.value })}
            placeholder="sk_live_... ou sk_test_..."
          />
          <Input
            label="Webhook Secret"
            type="password"
            value={stripeCredentials.webhookSecret}
            onChange={(e) => setStripeCredentials({ ...stripeCredentials, webhookSecret: e.target.value })}
            placeholder="whsec_..."
          />
        </div>
      </Modal>
    </div>
  );
};

export default SettingsPage;
